import { uuidv4 } from '@firebase/util';
import {
  ExtendFile,
  uploadImage,
  validateFile,
} from '@/components/file-thumbnail/utils';
import React, { useState } from 'react';
import UploadMultiFile, {
  UploadMultiFileProps,
} from '@/components/Upload/UploadMultiFile';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useDialog, useSnackbar } from '@/hooks/global-tools';
import { Accept } from 'react-dropzone';

type P = {
  value: ExtendFile[];
  setValue: React.Dispatch<React.SetStateAction<ExtendFile[]>>;
  isOnlyImage?: boolean;
  maxSize?: number;
  maxLength?: number;
  sortable?: boolean;
  upload?: boolean;
  isBase64?: boolean;
  accept?: Accept;
} & Pick<
  UploadMultiFileProps,
  'previewType' | 'useAddButton' | 'placeHolder' | 'disabled' | 'hideDropzone'
>;

export default function MultiFileUploader(props: P) {
  const {
    value,
    setValue,
    isOnlyImage = true,
    maxSize,
    maxLength,
    previewType,
    useAddButton,
    placeHolder,
    upload,
    sortable,
    disabled,
    isBase64,
    hideDropzone,
    accept,
  } = props;

  const [activeFile, setActiveFile] = useState<ExtendFile | null>(null);
  const dialog = useDialog();
  const toast = useSnackbar();

  const showAlert = (message: React.ReactNode) => {
    dialog.show({
      type: 'alert',
      message: message,
      isBlockBackDropClose: true,
    });
  };

  // ← base64 변환 헬퍼
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') resolve(reader.result);
        else reject(new Error('파일 변환에 실패했습니다.'));
      };
      reader.onerror = (err) => reject(err);
    });

  const handleAdd = async (acceptedFiles: File[]) => {
    // accept prop이 있을 때 파일 확장자 검증
    if (accept) {
      // Accept 타입에서 모든 허용된 확장자를 추출
      const allowedExtensions = Object.values(accept).flat();
      const hasInvalidFile = acceptedFiles.some((file) => {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        return !allowedExtensions.some(
          (ext) => ext.toLowerCase() === fileExtension,
        );
      });

      if (hasInvalidFile) {
        toast.showError({
          message: `${allowedExtensions.join(
            ', ',
          )} 확장자 파일만 첨부 가능합니다.`,
        });
        return;
      }
    }

    try {
      validateFile(acceptedFiles, {
        maxSize,
        isOnlyImage,
        maxLength,
        existFileLength: value.length,
      });
    } catch (e) {
      if (e instanceof Error) {
        return showAlert(e.message);
      } else {
        return showAlert('파일 업로드에 실패했습니다. 다시 시도해 주세요.');
      }
    }

    const newItems = acceptedFiles.map((file: File) => {
      const extendFile: ExtendFile = {
        id: uuidv4(),
        name: file.name,
        type: file.type,
        isUploaded: false,
        preview: URL.createObjectURL(file),
        status: 'new',
        file,
      };

      return extendFile;
    });

    // upload=false → base64 로직
    if (!upload) {
      if (isBase64) {
        const base64Items: ExtendFile[] = await Promise.all(
          acceptedFiles.map(async (file) => {
            const base64 = await fileToBase64(file);
            return {
              id: uuidv4(),
              name: file.name,
              type: file.type,
              isUploaded: false,
              preview: base64,
              status: 'new',
              file,
            } as ExtendFile;
          }),
        );
        return setValue([...value, ...base64Items]);
      } else {
        setValue([...value, ...newItems]);
        return;
      }
    }

    // 새 파일을 즉시 state에 추가 (isUploaded: false 상태로)
    setValue([...value, ...newItems]);

    // 각 파일을 개별적으로 업로드
    for (const item of newItems) {
      try {
        const { file } = item;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const uploadedFile = await uploadImage(file!);

        // 업로드 완료된 파일만 업데이트
        setValue((prevValue) =>
          prevValue.map((f) =>
            f.id === item.id
              ? {
                  ...f,
                  isUploaded: true,
                  file: undefined,
                  path: uploadedFile.url,
                  preview: uploadedFile.url,
                  name: uploadedFile.name,
                  uuid: uploadedFile.uuid,
                }
              : f,
          ),
        );
      } catch (error) {
        // 업로드 실패 시 해당 파일만 제거
        setValue((prevValue) => prevValue.filter((f) => f.id !== item.id));
        showAlert(
          <>
            파일 첨부에 실패했어요. 다시 시도해 주세요.
            <br />
            계속 오류가 발생할 경우 IT Service를 통해 오류를 접수해 주세요.
          </>,
        );
      }
    }
  };

  const handleRemove = (file: ExtendFile) => {
    let files = value;
    const search = files.filter((f) => f == file);
    if (search?.length) {
      if (search[0].status === 'new') {
        files = files.filter((f) => f !== file);
      } else {
        files.forEach((_file) => {
          if (_file == file) {
            _file.status = 'deleted';
          }
        });
      }
    }

    setValue([...files]);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    const search = value.filter((item) => item.id === active.id);
    if (search && search.length > 0) {
      setActiveFile(search[0]);
    }
  };

  const handleDragEnd = ({ over }: DragEndEvent) => {
    if (over && activeFile) {
      const activeIndex = value.findIndex((item) => item.id === activeFile.id);
      const overIndex = value.findIndex((item) => item.id === over.id);
      setValue(arrayMove(value, activeIndex, overIndex));
    }

    setActiveFile(null);
  };

  const handleDragCancel = () => {
    setActiveFile(null);
  };

  return (
    <UploadMultiFile
      files={value.filter((file) => file.status !== 'deleted')}
      onDrop={handleAdd}
      useAddButton={useAddButton}
      previewType={previewType}
      placeHolder={placeHolder}
      error={false}
      dragPreviewProps={{
        onDragEnd: handleDragEnd,
        onDragStart: handleDragStart,
        onDragCancel: handleDragCancel,
        onRemove: handleRemove,
        activeFile: activeFile,
      }}
      loading={false}
      sortable={sortable}
      disabled={disabled}
      hideDropzone={hideDropzone}
      accept={accept}
    />
  );
}
